import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Check, Trash2, Plus, Send, RefreshCcw, ExternalLink, Shield, Sparkles, MessageCircle, Webhook } from "lucide-react";
import { testAiProvider, AI_PROVIDERS, type AiProviderId } from "@/lib/chat.functions";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";

export const Route = createFileRoute("/_authenticated/admin/integrations")({
  component: IntegrationsAdmin,
});

function IntegrationsAdmin() {
  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Integrations"
        description="Connect external services. Bot-protection site keys live here; secret keys are configured server-side."
      />
      <Tabs defaultValue="bot" className="w-full">
        <PillTabsList search>
          <PillTabsTrigger value="bot"><Shield className="h-3.5 w-3.5 mr-1.5" />Bot protection</PillTabsTrigger>
          <PillTabsTrigger value="ai"><Sparkles className="h-3.5 w-3.5 mr-1.5" />AI providers</PillTabsTrigger>
          <PillTabsTrigger value="messaging"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />Messaging</PillTabsTrigger>
          <PillTabsTrigger value="webhooks"><Webhook className="h-3.5 w-3.5 mr-1.5" />Incoming webhooks</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="bot" className="mt-4"><BotProtectionCard /></TabsContent>
        <TabsContent value="ai" className="mt-4"><AiProvidersCard /></TabsContent>
        <TabsContent value="messaging" className="mt-4"><MessagingCard /></TabsContent>
        <TabsContent value="webhooks" className="mt-4"><WebhooksCard /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Settings shared helpers
// ============================================================

function useSetting<T = any>(key: string, fallback: T) {
  return useQuery({
    queryKey: ["site_settings", key],
    queryFn: async (): Promise<T> => {
      const { data } = await supabase.from("site_settings").select("value").eq("key", key).maybeSingle();
      return ((data?.value as T) ?? fallback) as T;
    },
  });
}

function useSaveSetting(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: any) => {
      const { error } = await supabase.from("site_settings").upsert({ key, value });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_settings", key] });
      toast.success("Saved");
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });
}

function Field({
  label, value, onChange, placeholder, type = "text",
}: { label: string; value?: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

// ============================================================
// Bot protection
// ============================================================

function BotProtectionCard() {
  const { data } = useSetting("integrations", {} as any);
  const save = useSaveSetting("integrations");
  const [state, setState] = useState<any>({});
  useEffect(() => { if (data) setState(data); }, [data]);
  const merge = (p: any) => setState((s: any) => ({ ...s, ...p }));

  const botEnabled = state.bot_protection_enabled ?? false;
  const recaptchaEnabled = state.recaptcha_enabled ?? false;
  const turnstileEnabled = state.turnstile_enabled ?? false;
  const activeProvider = (state.bot_protection_provider ?? "turnstile") as "recaptcha" | "turnstile";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Bot protection
          <Badge variant={botEnabled ? "default" : "secondary"}>
            {botEnabled ? "Enabled" : "Disabled"}
          </Badge>
        </CardTitle>
        <CardDescription>
          Toggle bot protection on or off, pick the active provider, and add the public site keys here.
          Set the matching secret keys as server-side environment variables
          (<code>RECAPTCHA_SECRET_KEY</code>, <code>TURNSTILE_SECRET_KEY</code>) in Cloud → Secrets.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Enable bot protection</Label>
            <p className="text-xs text-muted-foreground">
              Master switch. When off, no CAPTCHA challenge is shown on public forms.
            </p>
          </div>
          <Switch
            checked={botEnabled}
            onCheckedChange={(v) => merge({ bot_protection_enabled: v })}
          />
        </div>

        <div className={`space-y-4 ${botEnabled ? "" : "opacity-60 pointer-events-none"}`}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Google reCAPTCHA</Label>
                  {activeProvider === "recaptcha" && recaptchaEnabled && (
                    <Badge variant="outline" className="text-xs">Active</Badge>
                  )}
                </div>
                <Switch
                  checked={recaptchaEnabled}
                  onCheckedChange={(v) => merge({
                    recaptcha_enabled: v,
                    bot_protection_provider: v && !turnstileEnabled ? "recaptcha" : state.bot_protection_provider,
                  })}
                />
              </div>
              <Field label="Site key" value={state.recaptcha_site_key}
                onChange={(v) => merge({ recaptcha_site_key: v })} placeholder="6Lc..." />
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Cloudflare Turnstile</Label>
                  {activeProvider === "turnstile" && turnstileEnabled && (
                    <Badge variant="outline" className="text-xs">Active</Badge>
                  )}
                </div>
                <Switch
                  checked={turnstileEnabled}
                  onCheckedChange={(v) => merge({
                    turnstile_enabled: v,
                    bot_protection_provider: v && !recaptchaEnabled ? "turnstile" : state.bot_protection_provider,
                  })}
                />
              </div>
              <Field label="Site key" value={state.turnstile_site_key}
                onChange={(v) => merge({ turnstile_site_key: v })} placeholder="0x4A..." />
            </div>
          </div>

          {recaptchaEnabled && turnstileEnabled && (
            <div className="rounded-lg border p-4 space-y-2">
              <Label className="text-sm font-medium">Active provider</Label>
              <p className="text-xs text-muted-foreground">
                Both providers are enabled. Pick which one is used on public forms.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant={activeProvider === "recaptcha" ? "default" : "outline"}
                  onClick={() => merge({ bot_protection_provider: "recaptcha" })}
                >
                  reCAPTCHA
                </Button>
                <Button
                  size="sm"
                  variant={activeProvider === "turnstile" ? "default" : "outline"}
                  onClick={() => merge({ bot_protection_provider: "turnstile" })}
                >
                  Turnstile
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={() => save.mutate(state)} disabled={save.isPending}>Save bot protection</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// AI providers
// ============================================================

function AiProvidersCard() {
  const { data } = useSetting("integrations", {} as any);
  const save = useSaveSetting("integrations");
  const [state, setState] = useState<any>({});
  useEffect(() => { if (data) setState(data); }, [data]);
  const merge = (p: any) => setState((s: any) => ({ ...s, ...p }));

  const provider = (state.chat_provider ?? "gemini") as AiProviderId;
  const providerDef = AI_PROVIDERS.find((p) => p.id === provider) ?? AI_PROVIDERS[0];
  const model = state.chat_model ?? providerDef.defaultModel;

  const [testing, setTesting] = useState(false);
  async function test() {
    setTesting(true);
    try {
      const res = await testAiProvider({ data: { provider, model } });
      if (res.ok) toast.success(`Connection OK: ${res.reply || "ok"}`);
      else toast.error(res.error || "Test failed");
    } catch (e: any) {
      toast.error(e?.message ?? "Test failed");
    } finally { setTesting(false); }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          AI providers
          <Badge variant="secondary">{providerDef.managed ? "No key needed" : providerDef.env}</Badge>
        </CardTitle>
        <CardDescription>
          Pick the default LLM provider used by the public chat widget and assistants.
          AI providers use API keys stored as server environment secrets.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <Label>Default provider</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={provider}
              onChange={(e) => merge({ chat_provider: e.target.value, chat_model: "" })}
            >
              {AI_PROVIDERS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}{p.managed ? " (built-in)" : ` (${p.env})`}
                </option>
              ))}
            </select>
          </div>
          <Field label="Model"
            value={state.chat_model}
            onChange={(v) => merge({ chat_model: v })}
            placeholder={providerDef.defaultModel} />
        </div>
        <p className="text-xs text-muted-foreground">
          Default for {providerDef.label}: <code>{providerDef.defaultModel}</code>.
          {!providerDef.managed && <> Add <code>{providerDef.env}</code> in server secrets before using.</>}
        </p>
        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={test} disabled={testing || !model}>
            {testing ? "Testing…" : <><Send className="h-4 w-4 mr-2" />Test connection</>}
          </Button>
          <Button onClick={() => save.mutate(state)} disabled={save.isPending}>Save AI defaults</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Messaging channels
// ============================================================

function MessagingCard() {
  const { data } = useSetting("integrations", {} as any);
  const save = useSaveSetting("integrations");
  const [s, setS] = useState<any>({});
  useEffect(() => { if (data) setS(data); }, [data]);
  const merge = (p: any) => setS((cur: any) => ({ ...cur, ...p }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Messaging channels</CardTitle>
        <CardDescription>Floating contact buttons on the public site. Bot tokens must be set as server secrets.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* WhatsApp */}
        <ChannelRow
          title="WhatsApp Business"
          enabled={!!s.whatsapp_enabled}
          onToggle={(v) => merge({ whatsapp_enabled: v })}
          docs="Send messages via wa.me link. For Cloud API, set WHATSAPP_TOKEN + WHATSAPP_PHONE_NUMBER_ID as server secrets."
        >
          <Field label="Phone number (international, digits only)" value={s.whatsapp_number}
            onChange={(v) => merge({ whatsapp_number: v })} placeholder="14155552671" />
        </ChannelRow>

        {/* Telegram */}
        <ChannelRow
          title="Telegram Bot"
          enabled={!!s.telegram_enabled}
          onToggle={(v) => merge({ telegram_enabled: v })}
          docs="Public link uses t.me/{handle}. For Bot API, set TELEGRAM_BOT_TOKEN as a server secret."
        >
          <Field label="Handle (without @)" value={s.telegram_handle}
            onChange={(v) => merge({ telegram_handle: v })} placeholder="yourbrand" />
        </ChannelRow>

        {/* Messenger */}
        <ChannelRow
          title="Facebook Messenger"
          enabled={!!s.messenger_enabled}
          onToggle={(v) => merge({ messenger_enabled: v })}
          docs="Direct visitors to m.me link. For Messenger Platform, set FB_PAGE_ACCESS_TOKEN as a server secret."
        >
          <Field label="Messenger URL" value={s.messenger_url}
            onChange={(v) => merge({ messenger_url: v })} placeholder="https://m.me/yourpage" />
        </ChannelRow>

        <div className="flex justify-end">
          <Button onClick={() => save.mutate(s)} disabled={save.isPending}>Save messaging</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ChannelRow({
  title, enabled, onToggle, docs, children,
}: { title: string; enabled: boolean; onToggle: (v: boolean) => void; docs: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold">{title}</div>
          <p className="text-xs text-muted-foreground mt-1">{docs}</p>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} />
      </div>
      {enabled && <div className="grid gap-3 md:grid-cols-2 pt-2">{children}</div>}
    </div>
  );
}

// ============================================================
// Incoming webhooks
// ============================================================

type Endpoint = {
  id: string; name: string; slug: string; secret: string;
  event_type: string; enabled: boolean; description: string | null;
  created_at: string;
};

type Event = {
  id: string; endpoint_id: string | null; endpoint_slug: string;
  payload: any; signature_valid: boolean; source_ip: string | null; received_at: string;
};

function WebhooksCard() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", slug: "", event_type: "generic", description: "" });

  const { data: endpoints, isLoading } = useQuery({
    queryKey: ["webhook_endpoints"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webhook_endpoints").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as Endpoint[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!draft.name.trim() || !draft.slug.trim()) throw new Error("Name and slug required");
      const slug = draft.slug.toLowerCase().replace(/[^a-z0-9-]/g, "-");
      const secret = randomSecret();
      const { error } = await supabase.from("webhook_endpoints").insert({
        name: draft.name.trim(), slug, secret,
        event_type: draft.event_type || "generic",
        description: draft.description || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhook_endpoints"] });
      setDraft({ name: "", slug: "", event_type: "generic", description: "" });
      setCreating(false);
      toast.success("Endpoint created");
    },
    onError: (e: any) => toast.error(e.message ?? "Create failed"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("webhook_endpoints").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook_endpoints"] }),
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  const toggle = useMutation({
    mutationFn: async (e: Endpoint) => {
      const { error } = await supabase.from("webhook_endpoints").update({ enabled: !e.enabled }).eq("id", e.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook_endpoints"] }),
  });

  const rotate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("webhook_endpoints").update({ secret: randomSecret() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["webhook_endpoints"] }); toast.success("Secret rotated"); },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          Incoming webhooks
          <Button size="sm" variant="outline" onClick={() => setCreating((v) => !v)}>
            <Plus className="h-4 w-4 mr-1" />New endpoint
          </Button>
        </CardTitle>
        <CardDescription>
          External services post events to your site. Each endpoint has a unique URL and an HMAC-SHA-256 signing secret
          (header: <code>x-signature: sha256=&lt;hex&gt;</code>).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {creating && (
          <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/30">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Stripe checkout events" />
              <Field label="Slug (URL part)" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} placeholder="stripe-checkout" />
              <Field label="Event type" value={draft.event_type} onChange={(v) => setDraft({ ...draft, event_type: v })} placeholder="generic" />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea rows={2} value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="What this endpoint receives" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
              <Button size="sm" onClick={() => create.mutate()} disabled={create.isPending}>Create endpoint</Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading endpoints…</div>
        ) : !endpoints || !endpoints.length ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No webhook endpoints yet. Create one to start receiving events.
          </div>
        ) : (
          <div className="space-y-3">
            {endpoints.map((e) => (
              <EndpointRow
                key={e.id}
                endpoint={e}
                onToggle={() => toggle.mutate(e)}
                onDelete={() => { if (confirm(`Delete endpoint "${e.name}"?`)) del.mutate(e.id); }}
                onRotate={() => { if (confirm("Rotate signing secret? Existing senders will need the new value.")) rotate.mutate(e.id); }}
              />
            ))}
          </div>
        )}

        <RecentEvents />
      </CardContent>
    </Card>
  );
}

function EndpointRow({
  endpoint, onToggle, onDelete, onRotate,
}: { endpoint: Endpoint; onToggle: () => void; onDelete: () => void; onRotate: () => void }) {
  const url = `${typeof window !== "undefined" ? window.location.origin : ""}/api/public/webhooks/${endpoint.slug}`;
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold">
            {endpoint.name}
            <Badge variant={endpoint.enabled ? "default" : "secondary"}>{endpoint.enabled ? "Enabled" : "Disabled"}</Badge>
            <Badge variant="outline">{endpoint.event_type}</Badge>
          </div>
          {endpoint.description && <p className="text-xs text-muted-foreground mt-1">{endpoint.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch checked={endpoint.enabled} onCheckedChange={onToggle} />
          <Button variant="outline" size="icon" onClick={onRotate} title="Rotate signing secret">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={onDelete} title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <CopyRow label="Endpoint URL" value={url} icon={<ExternalLink className="h-3.5 w-3.5" />} />
        <CopyRow label="Signing secret" value={endpoint.secret} mono />
      </div>
    </div>
  );
}

function CopyRow({ label, value, mono, icon }: { label: string; value: string; mono?: boolean; icon?: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid gap-1">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {icon}{label}
      </Label>
      <div className="flex items-center gap-1">
        <Input readOnly value={value} className={mono ? "font-mono text-xs" : "text-xs"} />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          title="Copy"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function RecentEvents() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["webhook_events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webhook_events").select("*").order("received_at", { ascending: false }).limit(25);
      if (error) throw error;
      return data as Event[];
    },
  });

  return (
    <div className="space-y-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">Recent events</div>
        <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCcw className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading events…</div>
      ) : !data?.length ? (
        <div className="text-sm text-muted-foreground">No events received yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Received</th>
                <th className="px-3 py-2 text-left">Endpoint</th>
                <th className="px-3 py-2 text-left">Signature</th>
                <th className="px-3 py-2 text-left">Source IP</th>
                <th className="px-3 py-2 text-left">Payload preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((e) => (
                <tr key={e.id} className="align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{new Date(e.received_at).toLocaleString()}</td>
                  <td className="px-3 py-2 whitespace-nowrap"><code className="text-xs">{e.endpoint_slug}</code></td>
                  <td className="px-3 py-2">
                    <Badge variant={e.signature_valid ? "default" : "secondary"}>
                      {e.signature_valid ? "Valid" : "Unsigned/Invalid"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">{e.source_ip ?? "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono max-w-md truncate">
                    {e.payload ? JSON.stringify(e.payload).slice(0, 120) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function randomSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return "whsec_" + Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
